import React, { useRef, useEffect } from 'react';
import { Controller, type RegisterOptions, useFormContext } from 'react-hook-form';
import { Flex, Box, Textarea } from '@chakra-ui/react';
import { Field } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

type FormFieldInterface = {
	name: string;
	label?: string | React.ReactNode;
	isRequired?: boolean;
	helperText?: string | React.ReactNode;
};

type FormTextareaProps = FormFieldInterface & {
	registerOptions?: Partial<RegisterOptions>;
	textareaProps?: React.ComponentProps<typeof Textarea>;
	autoResize?: boolean;
	maxLength?: number;
	disabled?: boolean;
	showCharacterCount?: boolean;
	helperText?: React.ReactNode;
	label?: React.ReactNode;
};

export const FormTextarea: React.FC<FormTextareaProps> = props => {
	const { name, label, isRequired, registerOptions, textareaProps, helperText, autoResize, maxLength, disabled = false, showCharacterCount = false } = props;
	const { t } = useTranslation();
	const { control } = useFormContext();
	const ref = useRef<HTMLTextAreaElement | null>(null);
	const SCROLL_HEIGHT_EXTRA_PX = 3;
	const handleResize = () => {
		if (ref.current) {
			ref.current.style.height = 'auto';
			ref.current.style.height = `${ref.current.scrollHeight + SCROLL_HEIGHT_EXTRA_PX}px`;
		}
	};

	useEffect(() => {
		if (autoResize && ref.current) {
			handleResize();
			ref.current.addEventListener('input', handleResize);
			return () => {
				ref.current?.removeEventListener('input', handleResize);
			};
		}
	}, [autoResize]);

	return (
		<Controller
			control={control}
			name={name}
			rules={{
				...(isRequired && { required: t('general.required') }),
				...registerOptions,
			}}
			render={({ field, fieldState }) => {
				const value = typeof field.value === 'string' ? field.value : '';
				return (
					<Field.Root invalid={fieldState.invalid} required={isRequired}>
						{label && (
							<Field.Label htmlFor={name}>
								{label} <Field.RequiredIndicator />
							</Field.Label>
						)}
						<Textarea
							style={{ backgroundColor: 'white' }}
							disabled={disabled}
							{...field}
							ref={e => {
								field.ref(e);
								ref.current = e;
							}}
							maxLength={maxLength}
							id={name}
							autoComplete="off"
							resize={autoResize ? 'none' : 'initial'}
							{...textareaProps}
						/>
						{(helperText || showCharacterCount) && (
							<Field.HelperText>
								<Flex justify="space-between" gap={3}>
									<Box>{helperText}</Box>
									{showCharacterCount && maxLength && (
										<Box>
											{value.length} / {maxLength}
										</Box>
									)}
								</Flex>
							</Field.HelperText>
						)}
						<Field.ErrorText>{fieldState.error?.message}</Field.ErrorText>
					</Field.Root>
				);
			}}
		/>
	);
};
